import React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { GAME_MODE_CATALOG, EXAM_BLOCK_LABELS, type EvaluationBlock } from '@interactive-room';
import { ExamBlockInlineEditor, validateExamBlock, type ExamBlockExamContext } from './ExamBlockInlineEditor';
import { BlockTraceabilityIndicator } from './BlockTraceabilityIndicator';
import { guideBlockCollapsedHint } from './guide/GuideEditorialBlockEditors';
import type { GuideDesignMutation } from '../lib/guideDesignBadge';

interface ExamBlockAccordionProps {
  block: EvaluationBlock;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (block: EvaluationBlock) => void;
  onRemove: () => void;
  examContext?: ExamBlockExamContext;
  /** Soft document-like chrome for learning guides (less boxitis). */
  variant?: 'default' | 'guide';
  /** Mutaciones post-LLM atribuidas a este bloque (modo debug). */
  postLlmMutations?: GuideDesignMutation[];
}

function pedagogicalKindLabel(mode: string): string {
  switch (mode) {
    case 'subtitulo':
      return 'Sección';
    case 'destacado':
      return 'Idea clave';
    case 'figura':
      return 'Recurso visual';
    case 'glosario_tabla':
      return 'Glosario';
    case 'formula_destacada':
      return 'Fórmula';
    case 'info_block':
      return 'Lectura';
    case 'desarrollo':
      return 'Desarrollo';
    case 'arena':
      return 'Pregunta';
    case 'completar':
      return 'Completar';
    case 'vincula':
      return 'Emparejar';
    case 'order':
      return 'Ordenar';
    case 'agrupa':
      return 'Clasificar';
    default:
      return EXAM_BLOCK_LABELS[mode as keyof typeof EXAM_BLOCK_LABELS] ?? GAME_MODE_CATALOG[mode as keyof typeof GAME_MODE_CATALOG]?.displayName ?? 'Bloque';
  }
}

export function ExamBlockAccordion({
  block,
  index,
  open,
  onOpenChange,
  onChange,
  onRemove,
  examContext,
  variant = 'default',
  postLlmMutations = [],
}: ExamBlockAccordionProps) {
  const modeMeta = GAME_MODE_CATALOG[block.mode];
  const headline = block.label ?? EXAM_BLOCK_LABELS[block.mode] ?? modeMeta.displayName;
  const isGuide = variant === 'guide' || examContext?.resourceType === 'guide';
  const kindLabel = pedagogicalKindLabel(block.mode);
  const collapsedHint = isGuide
    ? guideBlockCollapsedHint(block.mode, (block.payload ?? {}) as Record<string, unknown>)
    : modeMeta.displayName;
  const mutationTooltip =
    postLlmMutations.length > 0
      ? postLlmMutations
          .map((m) => `${m.changeType}: ${m.reason} (${m.enforcer})`)
          .join('\n')
      : '';

  const handleOpenChange = (next: boolean) => {
    if (!next && open) {
      try {
        onChange(validateExamBlock(block));
      } catch {
        /* keep draft if invalid */
      }
    }
    onOpenChange(next);
  };

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      className={
        isGuide
          ? 'exam-block-accordion exam-block-accordion--guide overflow-hidden'
          : 'exam-block-accordion overflow-hidden rounded-xl border border-[var(--sala-border)]'
      }
    >
      <div
        className={
          isGuide
            ? 'exam-builder__block exam-builder__block--guide flex items-center gap-3 px-1 py-3'
            : 'exam-builder__block flex items-center gap-3 px-3 py-2'
        }
      >
        <CollapsiblePrimitive.Trigger className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className="font-display text-lg font-bold opacity-40">{index + 1}</span>
          {!isGuide ? <span className="text-lg">{modeMeta.icon}</span> : null}
          <div className="min-w-0 flex-1">
            <p className="flex min-w-0 items-center gap-1.5 truncate font-semibold text-sm">
              <span className="truncate">{headline}</span>
              <BlockTraceabilityIndicator payload={block.payload} />
              {postLlmMutations.length > 0 ? (
                <span
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800"
                  title={mutationTooltip}
                  aria-label={`${postLlmMutations.length} ajustes post-LLM`}
                >
                  ✎
                </span>
              ) : null}
            </p>
            <p className="truncate text-[11px] text-[var(--sala-ink-dim)]">
              {isGuide ? (
                <>
                  <span className="font-semibold text-[var(--sala-ink-muted)]">{kindLabel}</span>
                  {collapsedHint && collapsedHint !== headline ? (
                    <span> · {collapsedHint}</span>
                  ) : null}
                </>
              ) : (
                modeMeta.displayName
              )}
            </p>
          </div>
          <span className="text-lg font-light opacity-50">{open ? '−' : '+'}</span>
        </CollapsiblePrimitive.Trigger>
        <button
          type="button"
          className="shrink-0 text-xs text-red-400"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          Quitar
        </button>
      </div>

      <CollapsiblePrimitive.Content className="exam-block-accordion__content">
        <div
          className={
            isGuide
              ? 'exam-block-accordion__panel exam-block-accordion__panel--guide'
              : 'exam-block-accordion__panel'
          }
        >
          <ExamBlockInlineEditor block={block} onChange={onChange} examContext={examContext} />
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
